/**
 * شواهد بازیابی‌شده: تصمیم‌های تاریخی و شواهد تکمیلی.
 *
 * امتیاز نمایش داده می‌شود چون کاربر باید بتواند بفهمد چرا یک تصمیم بالا
 * نشسته. پنهان‌کردن امتیاز، بازیابی را جعبه‌ی سیاه می‌کرد.
 */

import { BookOpen, FileText, Landmark } from 'lucide-react'

import { Badge, Card, EmptyState, OrdinalBadge, SectionHeader } from '@/components/ui/primitives'
import { fa, faScore, orDash } from '@/lib/format'
import { labelOf } from '@/lib/labels'
import { useMeta } from '@/lib/meta-context'

import type { DecisionEvidence, HistoricalExample, SupplementaryEvidence } from '@/lib/types'

interface EvidenceViewProps {
  evidence: DecisionEvidence[]
  supplementary: SupplementaryEvidence[]
  examples: HistoricalExample[]
}

export const EvidenceView = ({ evidence, supplementary, examples }: EvidenceViewProps) => {
  const { meta } = useMeta()

  if (evidence.length === 0 && supplementary.length === 0) {
    return (
      <EmptyState
        title="شاهدی بازیابی نشد"
        detail="این معمولاً یعنی ایندکس خالی است یا پرونده با هیچ تصمیم تاریخی هم‌پوشانی ندارد."
        icon={<Landmark className="size-6" aria-hidden />}
      />
    )
  }

  return (
    <div className="space-y-4">
      {examples.length > 0 && (
        <Card>
          <SectionHeader
            title="نمونه‌های تاریخی مورد ارجاع مشاور"
            caption="این‌ها تصمیم‌هایی هستند که مشاور صریحاً به آن‌ها استناد کرده."
            icon={<BookOpen className="size-4" />}
          />
          <ul className="divide-y divide-line">
            {examples.map((example) => (
              <li key={example.decision_id} className="space-y-1 p-3">
                <p className="text-sm font-medium text-ink">
                  <span className="tabular">{example.decision_id}</span> — {example.summary}
                </p>
                <p className="text-xs leading-relaxed text-ink-muted">{example.relevance}</p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {evidence.length > 0 && (
        <Card>
          <SectionHeader
            title="تصمیم‌های بازیابی‌شده"
            caption={`${fa(evidence.length)} تصمیم، مرتب بر پایه‌ی امتیاز ترکیبی.`}
            icon={<Landmark className="size-4" />}
          />
          <ul className="divide-y divide-line">
            {evidence.map((item) => (
              <li key={item.decision.decision_id} className="space-y-2 p-3">
                <div className="flex items-start gap-2">
                  <OrdinalBadge value={item.rank} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink">{item.decision.title}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted">
                      <span className="tabular">{item.decision.decision_id}</span>
                      {item.decision.year !== null && <span>{fa(item.decision.year)}</span>}
                      <span>{orDash(item.decision.primary_domain)}</span>
                    </p>
                  </div>
                  <Badge tone="accent" className="tabular-nums">
                    {faScore(item.scores.final_score)}
                  </Badge>
                </div>
                {item.decision.one_line_summary !== '' && (
                  <p className="ps-7 text-xs leading-relaxed text-ink-muted">
                    {item.decision.one_line_summary}
                  </p>
                )}
                {item.reasons?.length > 0 && (
                  <div className="flex flex-wrap gap-1 ps-7">
                    {item.reasons.map((reason) => (
                      <Badge key={reason}>{reason}</Badge>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {supplementary.length > 0 && (
        <Card>
          <SectionHeader
            title="شواهد تکمیلی"
            caption="اصول تصمیم تأییدشده و بخش‌های سند پشتیبان."
            icon={<FileText className="size-4" />}
          />
          <ul className="divide-y divide-line">
            {supplementary.map((item) => (
              <li key={item.item.item_id} className="space-y-1.5 p-3">
                <div className="flex items-start gap-2">
                  <OrdinalBadge value={item.rank} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink">{item.item.title}</p>
                    <p className="mt-0.5 text-xs text-ink-muted">{item.item.citation}</p>
                  </div>
                  <Badge>{labelOf(meta.labels, 'supplementary_kind', item.item.kind)}</Badge>
                </div>
                <p className="ps-7 text-xs leading-relaxed text-ink-muted">{item.item.text}</p>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
